import { useContext, useEffect, useMemo, useRef } from 'react';
import { Skeleton } from '@mui/material';
import { Header, TopBar } from 'UI';
import Step from 'Domains/Step';
import StepHeader from 'Domains/StepHeader';
import { useTranslation } from 'react-i18next';
import formatTotalPrice from 'shared/formatTotalPrice';
import { centsToAmount } from 'shared/money';
import { useWidgetPriceQuote } from 'hooks/priceQuote';
import { WidgetContext } from '../../Widget.constants';
import { WidgetStep } from '../../Widget.types';
import * as S from './WidgetContent.styles';
import { getHeaderPriceView, getNextLastQuotedText } from './utils';

const WidgetContent = () => {
  const { state } = useContext(WidgetContext);
  const { t } = useTranslation();
  const { args, quote, isError } = useWidgetPriceQuote();
  const { currentStep, selectedAppointmentType, selectedRecurrenceType, widgetSteps } = state;
  const hideTopBar = [WidgetStep.PAYMENT_ERROR, WidgetStep.PAYMENT_SUCCESS].includes(currentStep);
  const lastQuotedPriceText = useRef<string>();

  const quotedPriceText = useMemo(
    () =>
      quote
        ? formatTotalPrice(t, centsToAmount(quote.priceCents), selectedRecurrenceType, selectedAppointmentType)
        : undefined,
    [quote, t, selectedRecurrenceType, selectedAppointmentType],
  );

  useEffect(() => {
    lastQuotedPriceText.current = getNextLastQuotedText(!!args, quotedPriceText, lastQuotedPriceText.current);
  }, [args, quotedPriceText]);

  const renderPrice = () => {
    const priceView = getHeaderPriceView({
      hasArgs: !!args,
      zeroText: formatTotalPrice(t, 0, selectedRecurrenceType, selectedAppointmentType),
      quotedText: quotedPriceText,
      lastQuotedText: lastQuotedPriceText.current,
      isError,
    });

    switch (priceView.kind) {
      case 'text':
        return <S.SummaryValue>{priceView.text}</S.SummaryValue>;
      case 'empty':
        return null;
      default:
        return <Skeleton variant="text" width={96} />;
    }
  };

  return (
    <S.Container>
      {hideTopBar ? null : <Header>{renderPrice()}</Header>}
      {hideTopBar ? null : <TopBar />}
      <S.BodyContainer>
        <StepHeader stepNumber={currentStep} />
        <Step stepNumber={currentStep} steps={widgetSteps} />
      </S.BodyContainer>
    </S.Container>
  );
};
export default WidgetContent;
